/**
 * ========================
 * BUDGETS DOMAIN MENU PROVIDER
 * ========================
 *
 * Menu configuration for the Budget Management domain.
 * Sections:
 *  1. Overview — Dashboard (all budget users)
 *  2. Budgets — All Budgets, Create Budget (all budget users)
 *  3. Approvals — Approval Queue, Change Requests (approvers)
 *  4. Monitoring — Alerts, Unbudgeted Spend, Analytics, Combined View (approvers)
 *  5. Administration — Settings & Thresholds, Lookups, AI Insights (admin)
 */

import {
  IconDashboard,
  IconListDetails,
  IconFilePlus,
  IconChecklist,
  IconBell,
  IconReceipt,
  IconSettings,
  IconDatabase,
  IconGitPullRequest,
  IconChartBar,
  IconTableOptions,
  IconSparkles,
  IconTrendingUp,
  IconChartLine,
  IconCashBanknote,
  IconArrowsDiff,
  IconAlertTriangle,
  IconBook,
  IconFileAnalytics,
} from '@tabler/icons-react';
import { DomainMenuProvider, MenuSection } from '../../layouts/menu/types';
import { createVisibilityCheck } from './BaseMenuProvider';
import { useBudgetBadgesStore } from '@/domain/badges/budgetBadgesStore';

class BudgetsMenuProvider implements DomainMenuProvider {
  domainId = 'budgets';

  getMenuConfig() {
    // Visibility tiers
    const allBudgetUsers = createVisibilityCheck({ roles: ['Admin', 'BudgetManager', 'BudgetAdmin'] });
    const approvers = createVisibilityCheck({ roles: ['Admin', 'BudgetAdmin'] });
    const adminsOnly = createVisibilityCheck({ roles: ['Admin'] });

    const sections: MenuSection[] = [
      // Overview
      {
        id: 'budgets-overview',
        label: 'Overview',
        items: [
          {
            id: 'budgets-dashboard',
            label: 'Dashboard',
            path: '/app/budgets',
            icon: <IconDashboard size={16} />,
            visible: allBudgetUsers,
          },
        ],
      },

      // Budgets
      {
        id: 'budgets-budgets',
        label: 'Budgets',
        items: [
          {
            id: 'budgets-all-budgets',
            label: 'All Budgets',
            path: '/app/budgets/list',
            icon: <IconListDetails size={16} />,
            visible: allBudgetUsers,
          },
          {
            id: 'budgets-create-budget',
            label: 'Create Budget',
            path: '/app/budgets/create',
            icon: <IconFilePlus size={16} />,
            visible: allBudgetUsers,
          },
        ],
      },

      // Approvals — approvers only
      {
        id: 'budgets-approvals',
        label: 'Approvals',
        visible: approvers,
        items: [
          {
            id: 'budgets-approval-queue',
            label: 'Approval Queue',
            path: '/app/budgets/approvals',
            icon: <IconChecklist size={16} />,
            visible: approvers,
            countSelector: () => useBudgetBadgesStore.getState().pendingBudgetApprovals || null,
            badgeColor: 'yellow',
          },
          {
            id: 'budgets-change-requests',
            label: 'Change Requests',
            path: '/app/budgets/change-requests',
            icon: <IconGitPullRequest size={16} />,
            visible: approvers,
            countSelector: () => useBudgetBadgesStore.getState().pendingChangeRequests || null,
            badgeColor: 'orange',
          },
        ],
      },

      // Monitoring — approvers only
      {
        id: 'budgets-monitoring',
        label: 'Monitoring',
        visible: approvers,
        items: [
          {
            id: 'budgets-alerts',
            label: 'Alerts',
            path: '/app/budgets/alerts',
            icon: <IconBell size={16} />,
            visible: approvers,
            countSelector: () => useBudgetBadgesStore.getState().unacknowledgedAlerts || null,
            badgeColor: 'red',
          },
          {
            id: 'budgets-unbudgeted',
            label: 'Unbudgeted Spend',
            path: '/app/budgets/unbudgeted',
            icon: <IconReceipt size={16} />,
            visible: approvers,
          },
          {
            id: 'budgets-analytics',
            label: 'Analytics',
            path: '/app/budgets/analytics',
            icon: <IconChartBar size={16} />,
            visible: approvers,
          },
          {
            id: 'budgets-analytics-combined',
            label: 'Combined View',
            path: '/app/budgets/analytics/combined',
            icon: <IconTableOptions size={16} />,
            visible: approvers,
          },
        ],
      },

      // Foresight — where the budgets are HEADED, as opposed to Monitoring's where-they've-been.
      {
        id: 'budgets-foresight',
        label: 'Foresight',
        visible: approvers,
        items: [
          {
            id: 'budgets-forecast',
            label: 'Forecast & Runway',
            path: '/app/budgets/forecast',
            icon: <IconTrendingUp size={16} />,
            visible: approvers,
          },
          {
            id: 'budgets-trends',
            label: 'Spend Trends',
            path: '/app/budgets/trends',
            icon: <IconChartLine size={16} />,
            visible: approvers,
          },
          {
            id: 'budgets-cashflow',
            label: 'Cashflow',
            path: '/app/budgets/cashflow',
            icon: <IconCashBanknote size={16} />,
            visible: approvers,
          },
          {
            id: 'budgets-variance',
            label: 'Plan vs Actual',
            path: '/app/budgets/variance',
            icon: <IconArrowsDiff size={16} />,
            visible: approvers,
          },
          {
            id: 'budgets-anomalies',
            label: 'Anomalies',
            path: '/app/budgets/anomalies',
            icon: <IconAlertTriangle size={16} />,
            visible: approvers,
            countSelector: () => useBudgetBadgesStore.getState().openAnomalies || null,
            badgeColor: 'red',
          },
          {
            id: 'budgets-ledger',
            label: 'Ledger Explorer',
            path: '/app/budgets/ledger',
            icon: <IconBook size={16} />,
            visible: approvers,
          },
          {
            // AI Insights moved OUT of Administration: it was gated adminsOnly in the menu but
            // Admin+BudgetAdmin in the router, so a BudgetAdmin could reach the page by URL and
            // never see the link to it.
            id: 'budgets-ai-insights',
            label: 'AI Insights',
            path: '/app/budgets/ai-insights',
            icon: <IconSparkles size={16} />,
            visible: approvers,
          },
        ],
      },

      // Reporting — open to managers, who build reports about their own budgets.
      {
        id: 'budgets-reporting',
        label: 'Reporting',
        visible: allBudgetUsers,
        items: [
          {
            id: 'budgets-reports',
            label: 'Report Builder',
            path: '/app/budgets/reports',
            icon: <IconFileAnalytics size={16} />,
            visible: allBudgetUsers,
          },
        ],
      },

      // Administration — Admin only
      {
        id: 'budgets-administration',
        label: 'Administration',
        visible: adminsOnly,
        items: [
          {
            id: 'budgets-settings',
            label: 'Settings & Thresholds',
            path: '/app/budgets/admin/settings',
            icon: <IconSettings size={16} />,
            visible: adminsOnly,
          },
          {
            id: 'budgets-lookups',
            label: 'Lookups',
            path: '/app/budgets/admin/lookups',
            icon: <IconDatabase size={16} />,
            visible: adminsOnly,
          },
        ],
      },
    ];

    return {
      domainId: this.domainId,
      sections,
      metadata: {
        version: '1.0.0',
        lastUpdated: Date.now(),
        description: 'Budget management menu',
      },
    };
  }
}

export const budgetsMenuProvider = new BudgetsMenuProvider();
